// ./src/components/features/Vouchers/VouchersFilterBar/VouchersFilterBar.tsx
//
// Filter controls for the Vouchers (coupon type) list: by status (active /
// disabled). Fully controlled — the page owns the value and maps it to a
// CouponTypeFilter.

import React from "react";
import { Group, SegmentedControl, Input, Button, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon } from "@/components/generic";

export type VoucherStatusFilter = "active" | "disabled";

export interface VouchersFilterValue {
  status: VoucherStatusFilter | null;
}

export interface VouchersFilterBarProps {
  value: VouchersFilterValue;
  onChange: (value: VouchersFilterValue) => void;
  disabled?: boolean;
  showClear?: boolean;
}

export const EMPTY_VOUCHERS_FILTER: VouchersFilterValue = {
  status: null,
};

const isFilterActive = (value: VouchersFilterValue): boolean => Boolean(value.status);

const STATUS_STYLE: React.CSSProperties = { flex: "1 1 260px", minWidth: 240 };

const ANY = "any";

const VouchersFilterBarComponent: React.FC<VouchersFilterBarProps> = ({
  value,
  onChange,
  disabled = false,
  showClear = true,
}) => {
  const { t } = useTranslation(["vouchers", "common"]);

  const statusOptions = [
    { value: ANY, label: t("vouchers:filters.status.any", { defaultValue: "Any" }) },
    { value: "active", label: t("vouchers:filters.status.active", { defaultValue: "Active" }) },
    { value: "disabled", label: t("vouchers:filters.status.disabled", { defaultValue: "Disabled" }) },
  ];

  const active = isFilterActive(value);

  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      <Box style={STATUS_STYLE}>
        <Input.Wrapper label={t("vouchers:filters.status.label", { defaultValue: "Status" })}>
          <SegmentedControl
            data={statusOptions}
            value={value.status ?? ANY}
            onChange={(next) =>
              onChange({ ...value, status: next === ANY ? null : (next as VoucherStatusFilter) })
            }
            disabled={disabled}
            fullWidth
            size="md"
            mt={4}
          />
        </Input.Wrapper>
      </Box>

      {active && showClear && (
        <Button
          variant="subtle"
          color="gray"
          size="md"
          onClick={() => onChange(EMPTY_VOUCHERS_FILTER)}
          disabled={disabled}
          leftSection={<AppIcon iconName="X" size={16} />}
        >
          {t("common:buttons.clear", { defaultValue: "Clear" })}
        </Button>
      )}
    </Group>
  );
};

export const VouchersFilterBar = React.memo(VouchersFilterBarComponent);
